import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Info, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { CoreOptionsCard } from './CoreOptionsCard';
import { AdditionalOptionsCard } from './AdditionalOptionsCard';
import { PricingModal } from './PricingModal';
import { AddOptionModal } from './AddOptionModal';
import { type OptionType } from '../types';
import { 
  CoreOption, 
  AdditionalOption, 
  PricingConfig
} from './types';

// Updated props interface to work with real API format
interface SimplifiedStepTwoProps {
  currency: string;
  onNext: () => void;
  onBack: () => void;
  // Accept real API format options
  options?: OptionType[];
  setOptions?: (options: OptionType[]) => void;
  // Keep backward compatibility with simplified format
  coreOptions?: CoreOption[];
  setCoreOptions?: (options: CoreOption[]) => void;
  additionalOptions?: AdditionalOption[];
  setAdditionalOptions?: (options: AdditionalOption[]) => void;
}

export function SimplifiedStepTwo({
  currency,
  onNext,
  onBack,
  options,
  setOptions,
  coreOptions,
  setCoreOptions,
  additionalOptions,
  setAdditionalOptions
}: SimplifiedStepTwoProps) {
  const [showAddOption, setShowAddOption] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingConfig, setPricingConfig] = useState<{
    optionId: string;
    isCore: boolean;
    optionType: string;
    pricing: PricingConfig;
  } | null>(null);

  // Prevent infinite loops by tracking conversion state
  const isConverting = useRef(false);
  const lastOptionsStringified = useRef<string>('');
  const lastSimplifiedStringified = useRef<string>('');

  // Helper functions to convert between API format and simplified format
  const convertApiOptionsToSimplified = (apiOptions: OptionType[]): { core: CoreOption[], additional: AdditionalOption[] } => {
    const core: CoreOption[] = [];
    const additional: AdditionalOption[] = [];

    console.log('🔍 [SimplifiedStepTwo] Converting API options:', apiOptions);

    apiOptions.forEach((option, index) => {
      const isCoreOption = option.label === 'Number of People' || option.label === 'Number of Days';
      
      if (isCoreOption && option.type === 'number') {
        console.log(`✅ [SimplifiedStepTwo] Converting core option: ${option.label}`);
        core.push({
          id: option.label.toLowerCase().replace(/\s+/g, '_'),
          label: option.label,
          type: 'number',
          required: option.required,
          min: 1,
          max: option.label === 'Number of People' ? 20 : 14,
          defaultValue: option.label === 'Number of People' ? 2 : 1,
          pricing: (option.pricing || []).map(p => ({
            type: p.type === 'flat' ? 'fixed' : p.type as 'fixed' | 'percentage' | 'per_person' | 'per_day' | 'per_person_day',
            value: p.value,
            description: p.description
          }))
        });
      } else {
        console.log(`✅ [SimplifiedStepTwo] Converting additional option: ${option.label} (type: ${option.type})`);
        additional.push({
          id: `option_${index}`,
          label: option.label,
          type: option.type === 'dropdown' ? 'select' : (option.type as 'checkbox' | 'text'),
          required: option.required,
          options: (option.type === 'dropdown' || option.type === 'select') && option.options ? 
            (option.options as any[]).map(opt => 
              typeof opt === 'string' ? { value: opt } : opt
            ) : undefined,
          pricing: (option.pricing || []).map(p => ({
            type: p.type === 'flat' ? 'fixed' : p.type as 'fixed' | 'percentage' | 'per_person' | 'per_day' | 'per_person_day',
            value: p.value,
            description: p.description
          }))
        });
      }
    });

    console.log('🎯 [SimplifiedStepTwo] Conversion result:', { core, additional });
    return { core, additional };
  };

  const convertSimplifiedToApiOptions = (core: CoreOption[], additional: AdditionalOption[]): OptionType[] => {
    const apiOptions: OptionType[] = [];

    // Convert core options
    core.forEach(coreOpt => {
      apiOptions.push({
        label: coreOpt.label,
        type: 'number',
        required: coreOpt.required,
        pricing: coreOpt.pricing
      });
    });

    // Convert additional options
    additional.forEach(addOpt => {
      apiOptions.push({
        label: addOpt.label,
        type: addOpt.type === 'select' ? 'dropdown' : addOpt.type,
        required: addOpt.required,
        // FIXED: Preserve dropdown choice pricing data
        options: addOpt.options?.map(opt => ({
          value: opt.value,
          pricing: opt.pricing || []
        })),
        pricing: addOpt.pricing
      });
    });

    return apiOptions;
  };

  // Initialize options from API format or use defaults
  useEffect(() => {
    // Prevent conversion loops
    if (isConverting.current) {
      console.log('🛑 [SimplifiedStepTwo] Skipping conversion - already converting');
      return;
    }

    // If we have API options (editing mode), convert them to simplified format
    if (options && options.length > 0 && setCoreOptions && setAdditionalOptions) {
      const optionsString = JSON.stringify(options);
      
      // Only convert if options actually changed
      if (optionsString !== lastOptionsStringified.current) {
        console.log('🔄 [SimplifiedStepTwo] Loading existing options from API:', options);
        lastOptionsStringified.current = optionsString;
        isConverting.current = true;
        
        const { core, additional } = convertApiOptionsToSimplified(options);
        setCoreOptions(core);
        setAdditionalOptions(additional);
        
        // Allow some time for state updates before allowing new conversions
        setTimeout(() => {
          isConverting.current = false;
        }, 100);
      }
    } 
    // If we're in simplified mode and no core options exist, set defaults
    else if (coreOptions && coreOptions.length === 0 && setCoreOptions && !isConverting.current) {
      console.log('🔄 [SimplifiedStepTwo] Setting default core options');
      setCoreOptions([
        {
          id: 'people',
          label: 'Number of People',
          type: 'number',
          required: true,
          min: 1,
          max: 20,
          defaultValue: 2,
          pricing: []
        },
        {
          id: 'days',
          label: 'Number of Days',
          type: 'number',
          required: true,
          min: 1,
          max: 14,
          defaultValue: 1,
          pricing: []
        }
      ]);
    }
  }, [options, coreOptions, setCoreOptions, setAdditionalOptions]);

  // Sync simplified options back to API format when they change
  useEffect(() => {
    // Prevent conversion loops
    if (isConverting.current) {
      console.log('🛑 [SimplifiedStepTwo] Skipping API sync - currently converting');
      return;
    }

    if (setOptions && coreOptions && additionalOptions) {
      const simplifiedString = JSON.stringify({ core: coreOptions, additional: additionalOptions });
      
      // Only sync if simplified options actually changed
      if (simplifiedString !== lastSimplifiedStringified.current) {
        console.log('🔄 [SimplifiedStepTwo] Syncing simplified options back to API format');
        lastSimplifiedStringified.current = simplifiedString;
        
        const apiOptions = convertSimplifiedToApiOptions(coreOptions, additionalOptions);
        setOptions(apiOptions);
      }
    }
  }, [coreOptions, additionalOptions, setOptions]);

  const currencySymbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';

  const renderPricingPreview = (pricing: any) => {
    if (!pricing.value) return null;
    
    switch (pricing.type) {
      case 'fixed':
        return `+${currencySymbol}${pricing.value}`;
      case 'percentage':
        return `+${pricing.value}%`;
      case 'per_person':
        return `+${currencySymbol}${pricing.value}/person`;
      case 'per_day':
        return `+${currencySymbol}${pricing.value}/day`;
      case 'per_person_day':
        return `+${currencySymbol}${pricing.value}/person/day`;
      default:
        return `+${currencySymbol}${pricing.value}`;
    }
  };

  const getDefaultPricing = (optionType: string, isCore: boolean): PricingConfig => {
    if (isCore) {
      return { type: 'per_person', value: 0, description: '' };
    }
    
    switch (optionType) {
      case 'checkbox':
        return { type: 'fixed', value: 0, description: 'Additional charge when selected' };
      case 'select':
        return { type: 'fixed', value: 0, description: 'Additional charge for this choice' };
      case 'text':
        return { type: 'fixed', value: 0, description: 'Additional charge for custom input' };
      default:
        return { type: 'fixed', value: 0, description: '' };
    }
  };

  const openPricingModal = (optionId: string, isCore: boolean, optionType: string) => {
    const defaultPricing = getDefaultPricing(optionType, isCore);
    setPricingConfig({
      optionId,
      isCore,
      optionType,
      pricing: defaultPricing
    });
    setShowPricingModal(true);
  };

  const savePricing = () => {
    if (!pricingConfig) return;

    const { optionId, isCore, pricing } = pricingConfig;
    console.log(`💾 [SimplifiedStepTwo] Saving pricing: ${optionId}, isCore: ${isCore}`);

    if (isCore && setCoreOptions) {
      const option = coreOptions?.find(opt => opt.id === optionId);
      if (option && coreOptions) {
        const newCoreOptions = coreOptions.map(opt => 
          opt.id === optionId 
            ? { ...opt, pricing: [...(opt.pricing || []), pricing] }
            : opt
        );
        console.log(`✅ [SimplifiedStepTwo] Updated core options with new pricing:`, newCoreOptions);
        setCoreOptions(newCoreOptions);
      }
    } else if (!isCore && setAdditionalOptions) {
      const option = additionalOptions?.find(opt => opt.id === optionId);
      if (option && additionalOptions) {
        const newAdditionalOptions = additionalOptions.map(opt => 
          opt.id === optionId 
            ? { ...opt, pricing: [...(opt.pricing || []), pricing] }
            : opt
        );
        console.log(`✅ [SimplifiedStepTwo] Updated additional options with new pricing:`, newAdditionalOptions);
        setAdditionalOptions(newAdditionalOptions);
      }
    }

    setShowPricingModal(false);
    setPricingConfig(null);
    toast.success('Pricing added successfully');
  };

  const removePricing = (optionId: string, isCore: boolean, pricingIndex: number) => {
    console.log(`🗑️ [SimplifiedStepTwo] Removing pricing: ${optionId}, isCore: ${isCore}, index: ${pricingIndex}`);
    
    if (isCore && setCoreOptions) {
      const option = coreOptions?.find(opt => opt.id === optionId);
      if (option && coreOptions) {
        const newCoreOptions = coreOptions.map(opt => 
          opt.id === optionId 
            ? { ...opt, pricing: opt.pricing?.filter((_, idx) => idx !== pricingIndex) || [] }
            : opt
        );
        console.log(`✅ [SimplifiedStepTwo] Updated core options:`, newCoreOptions);
        setCoreOptions(newCoreOptions);
      }
    } else if (!isCore && setAdditionalOptions) {
      const option = additionalOptions?.find(opt => opt.id === optionId);
      if (option && additionalOptions) {
        const newAdditionalOptions = additionalOptions.map(opt => 
          opt.id === optionId 
            ? { ...opt, pricing: opt.pricing?.filter((_, idx) => idx !== pricingIndex) || [] }
            : opt
        );
        console.log(`✅ [SimplifiedStepTwo] Updated additional options:`, newAdditionalOptions);
        setAdditionalOptions(newAdditionalOptions);
      }
    }
    toast.success('Pricing removed');
  };

  const handleAddOption = (option: AdditionalOption) => {
    console.log(`➕ [SimplifiedStepTwo] Adding new option:`, option);
    if (setAdditionalOptions && additionalOptions) {
      const newAdditionalOptions = [...additionalOptions, option];
      console.log(`✅ [SimplifiedStepTwo] Updated additional options:`, newAdditionalOptions);
      setAdditionalOptions(newAdditionalOptions);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">
              Adventure Options
            </h3>
            <p className="text-slate-600 mt-1">
              Configure what customers can choose when booking
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="p-4 rounded-xl border bg-gradient-to-r from-emerald-50 to-emerald-50 border-blue-200">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-slate-900 mb-1">
                Keep it simple! Start with the basics and add more options as needed.
              </p>
              <p className="text-slate-600">
                <strong className="text-blue-700">People</strong> and <strong className="text-emerald-700">Days</strong> automatically multiply per-person and per-day pricing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Core Options */}
      {coreOptions && setCoreOptions && (
        <CoreOptionsCard
          coreOptions={coreOptions}
          setCoreOptions={setCoreOptions}
          currency={currency}
          onAddPricing={openPricingModal}
          onRemovePricing={removePricing}
          renderPricingPreview={renderPricingPreview}
        />
      )}

      {/* Additional Options */}
      {additionalOptions && setAdditionalOptions && (
        <AdditionalOptionsCard
          additionalOptions={additionalOptions}
          setAdditionalOptions={setAdditionalOptions}
          currency={currency}
          onAddPricing={openPricingModal}
          onRemovePricing={removePricing}
          renderPricingPreview={renderPricingPreview}
          onShowAddOption={() => setShowAddOption(true)}
        />
      )}

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        pricingConfig={pricingConfig}
        setPricingConfig={setPricingConfig}
        onSave={savePricing}
        currency={currency}
        renderPricingPreview={renderPricingPreview}
      />

      {/* Add Option Modal */}
      <AddOptionModal
        isOpen={showAddOption}
        onClose={() => setShowAddOption(false)}
        onAddOption={handleAddOption}
      />

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-slate-200 shadow-sm hover:shadow-md transition-shadow"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Details
        </Button>
        <Button
          onClick={onNext}
          className="bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 shadow-md hover:shadow-lg transition-shadow"
        >
          Continue to Availability
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
} 